import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  BadgeCheck,
  MessageSquare,
  Images,
  Video,
  FileText,
  Star,
  Loader2,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { fetchPerfilPublico } from "../../lib/profile";
import { fetchPortafolioDeUsuario } from "../../lib/portafolio";
import { ConexionAPI } from "../../lib/conexion";
import { humanizeAxiosError } from "../../lib/api";
import AuthedImage from "../../components/AuthedImage";
import { PlanBadge } from "../../components/PlanBadge";
import { EmbajadorBadge } from "../../components/EmbajadorBadge";

const TIPO_LABEL = {
  cliente: "Cliente",
  maestro: "Maestro-Contratista",
  profesional: "Maestro-Contratista",
  contratista: "Maestro-Contratista",
  ferreteria: "Ferretería",
};

export default function ConexionPerfilPublico() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [portafolio, setPortafolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contacting, setContacting] = useState(false);

  const esPropio = user?.id === userId;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [p, port] = await Promise.all([
          fetchPerfilPublico(userId),
          fetchPortafolioDeUsuario(userId).catch(() => ({ items: [] })),
        ]);
        if (cancelled) return;
        setPerfil(p);
        setPortafolio(port.items || []);
      } catch (e) {
        if (!cancelled) setError(humanizeAxiosError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  async function contactar() {
    if (esPropio) return;
    setContacting(true);
    try {
      const { conversacion_id } = await ConexionAPI.abrirConversacion(userId);
      navigate(`/conexion/mensajes?conv=${conversacion_id}`);
    } catch (e) {
      setError(humanizeAxiosError(e));
    } finally {
      setContacting(false);
    }
  }

  if (loading) {
    return (
      <div className="text-center py-16 text-slate-500 text-sm" data-testid="conexion-perfil-loading">
        <Loader2 size={22} className="mx-auto animate-spin" />
        <p className="mt-2">Cargando perfil…</p>
      </div>
    );
  }

  if (error || !perfil) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16" data-testid="conexion-perfil-error">
        <p className="text-slate-600 font-semibold">{error || "Perfil no encontrado."}</p>
        <button onClick={() => navigate(-1)} className="mt-3 inline-block text-brand-red font-semibold hover:underline">
          Volver
        </button>
      </div>
    );
  }

  const initials = (perfil.nombre_completo || "CL")
    .split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const badges = Object.entries(perfil.verificado || {}).filter(([, v]) => v).map(([k]) => k);
  const destacados = portafolio.filter((i) => i.destacado);
  const ordenados = [...destacados, ...portafolio.filter((i) => !i.destacado)];

  return (
    <div className="max-w-5xl mx-auto" data-testid={`conexion-perfil-${userId}`}>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft size={16} /> Volver
      </button>

      {/* Cabecera del profesional */}
      <article className="bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-brand-navy to-brand-blue" />
        <div className="px-6 md:px-8 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-end gap-5">
            <div className="w-24 h-24 -mt-12 rounded-2xl overflow-hidden border-4 border-white bg-white shadow-md flex-shrink-0">
              {perfil.foto_url ? (
                <AuthedImage
                  url={perfil.foto_url}
                  alt={perfil.nombre_completo}
                  className="w-full h-full object-cover"
                  testid="conexion-perfil-avatar"
                  fallback={<div className="w-full h-full bg-gradient-to-br from-brand-blue to-brand-navy text-white text-3xl font-bold flex items-center justify-center">{initials}</div>}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-brand-blue to-brand-navy text-white text-3xl font-bold flex items-center justify-center">
                  {initials}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight inline-flex items-center gap-2 flex-wrap" data-testid="conexion-perfil-nombre">
                {perfil.nombre_completo}
                <PlanBadge plan={perfil.membresia_plan} size="lg" />
                <EmbajadorBadge activo={perfil.es_embajador} size="lg" />
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600">
                <span className="font-semibold text-brand-navy">{TIPO_LABEL[perfil.tipo_usuario] || perfil.tipo_usuario}</span>
                {perfil.ubicacion_publica && (
                  <span className="inline-flex items-center gap-1 text-slate-500" data-testid="conexion-perfil-ubic">
                    <MapPin size={14} /> {perfil.ubicacion_publica}
                  </span>
                )}
              </div>
              {badges.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="conexion-perfil-badges">
                  {badges.map((b) => (
                    <span key={b} className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5 capitalize">
                      <BadgeCheck size={12} /> {b}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {!esPropio && (
              <button
                onClick={contactar}
                disabled={contacting}
                data-testid="conexion-perfil-contactar"
                className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-red-hover disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors shadow-sm self-start sm:self-auto"
              >
                <MessageSquare size={16} /> {contacting ? "Abriendo chat…" : "Contactar"}
              </button>
            )}
          </div>

          {perfil.descripcion_corta && (
            <p className="mt-5 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="conexion-perfil-descripcion">
              {perfil.descripcion_corta}
            </p>
          )}
        </div>
      </article>

      {/* Vitrina pública (Portafolio) */}
      <section className="mt-6" data-testid="conexion-perfil-vitrina">
        <div className="flex items-center gap-2 mb-4">
          <Images size={20} className="text-brand-navy" />
          <h2 className="text-lg font-bold text-slate-900">Vitrina de trabajos</h2>
          <span className="text-sm text-slate-400 font-medium">({ordenados.length})</span>
        </div>

        {ordenados.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center text-slate-500" data-testid="conexion-perfil-vitrina-vacia">
            <Images size={28} className="mx-auto text-slate-300" />
            <p className="mt-2 text-sm">Este profesional aún no ha publicado trabajos en su vitrina.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ordenados.map((item) => (
              <VitrinaCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function VitrinaCard({ item }) {
  return (
    <article className="bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden flex flex-col" data-testid={`vitrina-item-${item.id}`}>
      <div className="aspect-video bg-slate-100 relative">
        {item.portada_url ? (
          <AuthedImage
            url={item.portada_url}
            alt={item.titulo}
            className="w-full h-full object-cover"
            testid={`vitrina-portada-${item.id}`}
            fallback={<div className="w-full h-full flex items-center justify-center text-slate-400"><Images size={30} /></div>}
          />
        ) : item.video_url ? (
          <a
            href={item.video_url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-slate-500 bg-slate-900/5 hover:bg-slate-900/10 transition-colors"
            data-testid={`vitrina-video-${item.id}`}
          >
            <Video size={30} className="text-brand-red" />
            <span className="text-xs font-semibold">Ver video</span>
          </a>
        ) : item.categoria === "certificado" ? (
          <div className="w-full h-full flex items-center justify-center text-slate-400"><FileText size={30} /></div>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400"><Images size={30} /></div>
        )}
        {item.destacado && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-brand-red text-white text-[10px] font-bold px-2 py-0.5">
            <Star size={11} /> Destacado
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-sm font-bold text-slate-900 truncate">{item.titulo}</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          {item.anio ? `${item.anio}` : ""}
          {item.ubicacion_texto ? `${item.anio ? " · " : ""}${item.ubicacion_texto}` : ""}
        </p>
        {item.descripcion && (
          <p className="text-xs text-slate-600 mt-1.5 line-clamp-2">{item.descripcion}</p>
        )}
      </div>
    </article>
  );
}
